import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery, useMutation } from 'hooks/apollo';
import { gql } from '@apollo/client';

import { Button, Tag, Input, Form } from 'antd';
import { PlusOutlined, EditFilled } from '@ant-design/icons';
import Page from 'components/Page';
import Table from 'components/Table';
import Drawer from 'components/Drawer';

import globalMessages from 'translations/messages';
import useGlobalContext from 'hooks/useGlobalContext';
import RoleTypeLabel from 'components/RoleTypeLabel';
import messages from './messages';
import UpsertRoleForm from './UpsertRoleForm';

const QUERY = gql`
  query GetRoles {
    roles {
      id
      roleType
      name
      description
      permissions
      visible
    }
  }
`;

const CREATE_ROLE_MUTATUION = gql`
  mutation CreateRole($input: RoleInput!) {
    createRole(input: $input) {
      id
      roleType
      name
      description
      permissions
      visible
    }
  }
`;

const UPDATE_ROLE_MUTATUION = gql`
  mutation UpdateRole($id: ID!, $input: RoleInput) {
    updateRole(id: $id, input: $input) {
      id
      roleType
      name
      description
      permissions
      visible
    }
  }
`;

const Roles = () => {
  const { formatMessage: $t } = useIntl();

  const [search, setSearch] = useState();
  const { data, loading, refetch } = useQuery(QUERY);
  const [createRole, { loading: creating }] = useMutation(CREATE_ROLE_MUTATUION);
  const [updateRole, { loading: updating }] = useMutation(UPDATE_ROLE_MUTATUION);

  const [upsertRole, setUpsertRole] = useState();
  const [upsertForm] = Form.useForm();
  const { message } = useGlobalContext();

  const onUpsert = async (id, values) => {
    try {
      if (!id) {
        await createRole({
          variables: { input: { ...values } },
        });
        refetch();
      } else {
        await updateRole({
          variables: { id, input: { ...values } },
        });
      }
      setUpsertRole(false);
      message.success($t(globalMessages.successfullFormFeedback));
    } catch (error) {
      message.error('failed to create or update role');
    }
  };

  const columns = [
    {
      dataIndex: 'roleType',
      title: <FormattedMessage {...globalMessages.type} />,
      defaultSortOrder: 'ascend',
      render: (roleType) => <RoleTypeLabel roleType={roleType} />,
    },
    {
      dataIndex: 'name',
      title: <FormattedMessage {...globalMessages.name} />,
      render: (name, role) => (
        <a href="#" onClick={() => setUpsertRole(role)}>{name}</a>
      ),
    },
    {
      dataIndex: 'description',
      title: <FormattedMessage {...globalMessages.description} />,
    },
    {
      dataIndex: 'permissions',
      title: <FormattedMessage {...messages.permissions} />,
      render: (permissions) => permissions.map((permission, i) => (
        <Tag key={i} style={{ marginBottom: 8 }}>{permission}</Tag>),
      ),
    },
    {
      dataIndex: 'visible',
      title: <FormattedMessage {...messages.visible} />,
      type: 'boolean',
    },
  ];

  const actions = (role) => (
    <Button icon={<EditFilled />} onClick={() => setUpsertRole(role)}>
      <FormattedMessage {...globalMessages.edit} />
    </Button>
  );

  return (
    <Page>
      <Page.Header
        title={<FormattedMessage {...messages.authRoles} />}
        extra={(
          <Button type="primary" onClick={() => setUpsertRole({})} icon={<PlusOutlined />}>
            <FormattedMessage {...messages.createRole} />
          </Button>
        )}
      />
      <Page.Content>
        <Input.Search
          onSearch={setSearch}
          allowClear
          placeholder={$t(globalMessages.search)}
          style={{ marginBottom: '1em' }}
        />

        <Table
          loading={loading}
          columns={columns}
          dataSource={data?.roles}
          search={search}
          actions={actions}
        />
      </Page.Content>
      <Drawer
        title={
          upsertRole?.id
            ? <FormattedMessage {...messages.editRole} />
            : <FormattedMessage {...messages.createRole} />
        }
        open={!!upsertRole}
        onClose={() => setUpsertRole(false)}
        onCancel={() => setUpsertRole(false)}
        onOk={upsertForm.submit}
        confirmLoading={creating || updating}
      >
        <UpsertRoleForm role={upsertRole} form={upsertForm} onSubmit={onUpsert} />
      </Drawer>
    </Page>
  );
};

export default Roles;
